import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import WorkspaceOverview, { type WorkspaceMetric } from './dashboard/WorkspaceOverview';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const GestorJuridicoWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [todosPlanos, setTodosPlanos] = useState<PlanoTrabalho[]>([]);
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(() => new Set(['acoes-rapidas']));

  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);
  const renderCollapseButton = (id: string) => (
    <button
      type="button"
      onClick={() => toggleBox(id)}
      className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
    >
      {isBoxCollapsed(id) ? 'Expandir' : 'Recolher'}
    </button>
  );
  const fixedCardClass = 'flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border shadow-sm';
  const fixedCardBodyClass = 'min-h-0 flex-1 overflow-y-auto';

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        // minhaFila=false: todos na PGM para ter todos os números
        const data = await planoService.listAll({ minhaFila: false }).catch(() => []);
        if (!active) return;
        setTodosPlanos(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Nao foi possivel carregar a fila juridica.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const stats = useMemo(() => {
    const semProcurador = todosPlanos.filter(p => !p.procuradorJuridicoId).length;
    const comProcurador = todosPlanos.filter(p => Boolean(p.procuradorJuridicoId)).length;
    return { total: todosPlanos.length, semProcurador, comProcurador };
  }, [todosPlanos]);

  const planosParaDistribuir = useMemo(
    () => todosPlanos.filter(p => !p.procuradorJuridicoId).slice(0, 6),
    [todosPlanos]
  );

  const cards: WorkspaceMetric[] = [
    { label: 'Total na etapa PGM', value: stats.total, tone: 'neutral' },
    { label: 'A distribuir', value: stats.semProcurador, tone: 'warning' },
    { label: 'Com procurador vinculado', value: stats.comProcurador, tone: 'success' },
  ];

  return (
    <div className="w-full space-y-6 dark:[&_h1]:text-white dark:[&_h2]:text-white dark:[&_.bg-white]:bg-slate-950 dark:[&_.bg-white\\/90]:bg-slate-950/90 dark:[&_.bg-slate-50]:bg-slate-900 dark:[&_.border-slate-200]:border-slate-800 dark:[&_.text-slate-900]:text-slate-100 dark:[&_.text-slate-800]:text-slate-200 dark:[&_.text-slate-600]:text-slate-400 dark:[&_.text-slate-500]:text-slate-400 dark:[&_tr:hover]:bg-slate-900">
      <WorkspaceOverview
        profile="Gestor Jurídico"
        description="Distribuição da fila jurídica e acompanhamento dos procuradores."
        metrics={cards}
        loading={loading}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <div className={`${fixedCardClass} border-slate-200 bg-white`}>
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600">
                Planos aguardando distribuicao
              </h2>
              <p className="mt-1 text-sm text-slate-500">Sem procurador vinculado.</p>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/dashboard/gestor-juridico')}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Abrir fila completa
              </button>
              {renderCollapseButton('planos-distribuicao')}
            </div>
          </div>

          {!isBoxCollapsed('planos-distribuicao') && (loading ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">Carregando...</div>
          ) : error ? (
            <div className="px-5 py-10 text-center text-sm text-red-700">{error}</div>
          ) : planosParaDistribuir.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              {stats.total === 0
                ? 'Nenhum plano na etapa PGM no momento.'
                : 'Todos os planos ja possuem procurador vinculado.'}
            </div>
          ) : (
            <div className={`${fixedCardBodyClass} overflow-x-auto`}>
              <table className="w-full text-sm">
                <thead className="bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="px-5 py-3 text-left font-semibold text-slate-700">Plano</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Protocolo</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Atualizado</th>
                    <th className="px-5 py-3 text-right font-semibold text-slate-700">Acao</th>
                  </tr>
                </thead>
                <tbody>
                  {planosParaDistribuir.map(item => (
                    <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80">
                      <td className="px-5 py-3 align-top">
                        <div className="font-semibold text-slate-900">{item.titulo || 'Nao informado'}</div>
                        {item.emendaCodigo && (
                          <div className="text-xs text-slate-500">Emenda {item.emendaCodigo}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">{item.numeroProtocolo || '-'}</td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        {formatDate(item.dataAtualizacao || item.enviadoEm)}
                      </td>
                      <td className="px-5 py-3 align-top text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/plano/full/${item.id}`)}
                          className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-800"
                        >
                          Vincular procurador
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className={`${fixedCardClass} border-slate-200 bg-white`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-slate-900">Acoes rapidas</h2>
            {renderCollapseButton('acoes-rapidas')}
          </div>
          {!isBoxCollapsed('acoes-rapidas') && (
          <div className={`${fixedCardBodyClass} space-y-3 px-5 pb-4`}>
            <button
              type="button"
              onClick={() => navigate('/dashboard/gestor-juridico')}
              className="w-full rounded-2xl border border-slate-200 px-4 py-4 text-left transition hover:border-blue-200 hover:bg-blue-50"
            >
              <p className="text-sm font-semibold text-slate-900">Fila juridica completa</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Gerenciar a distribuicao de todos os planos na etapa PGM.
              </p>
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard/gestor-juridico?aba=todos')}
              className="w-full rounded-2xl border border-slate-200 px-4 py-4 text-left transition hover:border-blue-200 hover:bg-blue-50"
            >
              <p className="text-sm font-semibold text-slate-900">Ver todos os planos na PGM</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Visualizar todos os planos, incluindo os ja atribuidos.
              </p>
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestorJuridicoWorkspace;
